import { useMemo } from "react";
import {
  useGetCoachesByOrgIdsQuery,
  useGetLearnersByOrgIdsQuery,
  useGetOrgUsersByOrgQuery,
  useGetUserOrgScopeForChatQuery,
} from "graphql/__generated__/hooks";
import { ROLES } from "types/user";

export type ChatParticipantOption = {
  userId: string;
  displayName: string;
  roleLabel?: string;
};

type Args = {
  open: boolean;
  currentUserId: string;
  role: string | undefined;
  /** Admin: selected org; Director/Manager: user's org — everyone in that org + grad-class learners. */
  orgIdForOrgWideList: string | undefined;
};

function mergeOrgIdsFromScope(
  data:
    | {
        membership_orgs?: Array<{ org_id?: unknown } | null> | null;
        grad_class_orgs?: Array<{
          program?: { org_id?: unknown } | null;
        } | null> | null;
      }
    | undefined,
): string[] {
  if (!data) return [];
  const fromMembership =
    data.membership_orgs
      ?.map((m) => m?.org_id)
      .filter((id): id is string => Boolean(id)) ?? [];
  const fromGrad =
    data.grad_class_orgs
      ?.map((g) => g?.program?.org_id)
      .filter((id): id is string => Boolean(id)) ?? [];
  return [...new Set([...fromMembership, ...fromGrad])];
}

export function useChatParticipantOptions({
  open,
  currentUserId,
  role,
  orgIdForOrgWideList,
}: Args) {
  const skipBase = !open || !currentUserId;

  const isOrgWide =
    role === ROLES.ADMIN ||
    role === ROLES.DIRECTOR ||
    role === ROLES.MANAGER;

  // Admins choose an organization in the sidebar; every other role is scoped by
  // their own membership rows, which are the source of truth for org access.
  const { data: userOrgScopeData, loading: loadingUserOrgScope } =
    useGetUserOrgScopeForChatQuery({
      variables: { userId: currentUserId },
      skip: skipBase || role === ROLES.ADMIN,
    });

  const membershipOrgIds = useMemo(
    () => mergeOrgIdsFromScope(userOrgScopeData),
    [userOrgScopeData],
  );

  /**
   * Directors and managers fall back to their membership org, because the
   * profile org on the session user is not always populated.
   */
  const orgWideOrgId =
    role === ROLES.ADMIN
      ? orgIdForOrgWideList
      : orgIdForOrgWideList ?? membershipOrgIds[0];

  const { data: orgUsersData, loading: loadingOrgWide } =
    useGetOrgUsersByOrgQuery({
      variables: { orgId: orgWideOrgId || "" },
      skip: skipBase || !isOrgWide || !orgWideOrgId,
    });

  /** Same as coach: org_user membership + orgs from grad classes (for learners without org rows). */
  const { data: orgWideLearnersData, loading: loadingOrgWideLearners } =
    useGetLearnersByOrgIdsQuery({
      variables: {
        orgIds: orgWideOrgId ? [orgWideOrgId] : [],
      },
      skip: skipBase || !isOrgWide || !orgWideOrgId,
    });

  const { data: coachLearnersData, loading: loadingCoachLearners } =
    useGetLearnersByOrgIdsQuery({
      variables: { orgIds: membershipOrgIds },
      skip:
        skipBase ||
        role !== ROLES.COACH ||
        membershipOrgIds.length === 0,
    });

  const { data: coachesData, loading: loadingCoaches } =
    useGetCoachesByOrgIdsQuery({
      variables: { orgIds: membershipOrgIds },
      skip:
        skipBase ||
        role !== ROLES.STUDENT ||
        membershipOrgIds.length === 0,
    });

  const coachLearnerOptions = useMemo((): ChatParticipantOption[] => {
    if (role !== ROLES.COACH || !coachLearnersData) return [];
    const byId = new Map<string, ChatParticipantOption>();
    for (const gc of coachLearnersData.graduation_class ?? []) {
      for (const row of gc.students ?? []) {
        const s = row.student;
        if (!s?.user_id || s.user_id === currentUserId) continue;
        if (s.role !== ROLES.STUDENT) continue;
        if (byId.has(s.user_id)) continue;
        byId.set(s.user_id, {
          userId: s.user_id,
          displayName: s.name ?? s.email ?? s.user_id,
          roleLabel: s.role,
        });
      }
    }
    return [...byId.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [role, coachLearnersData, currentUserId]);

  const studentCoachOptions = useMemo((): ChatParticipantOption[] => {
    if (role !== ROLES.STUDENT || !coachesData) return [];
    const byId = new Map<string, ChatParticipantOption>();
    for (const ou of coachesData.organization_user ?? []) {
      const u = ou.user;
      if (!u?.user_id || u.user_id === currentUserId) continue;
      if (byId.has(u.user_id)) continue;
      byId.set(u.user_id, {
        userId: u.user_id,
        displayName: u.name ?? u.email ?? u.user_id,
        roleLabel: u.role,
      });
    }
    return [...byId.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [role, coachesData, currentUserId]);

  const orgWideOptions = useMemo((): ChatParticipantOption[] => {
    if (!isOrgWide) return [];
    const byId = new Map<string, ChatParticipantOption>();

    for (const ou of orgUsersData?.organization_user ?? []) {
      if (!ou.user_id || ou.user_id === currentUserId) continue;
      byId.set(ou.user_id, {
        userId: ou.user_id,
        displayName: ou.user?.name ?? ou.user?.email ?? ou.user_id,
        roleLabel: ou.user?.role,
      });
    }

    for (const gc of orgWideLearnersData?.graduation_class ?? []) {
      for (const row of gc.students ?? []) {
        const s = row.student;
        if (!s?.user_id || s.user_id === currentUserId) continue;
        if (s.role !== ROLES.STUDENT) continue;
        if (byId.has(s.user_id)) continue;
        byId.set(s.user_id, {
          userId: s.user_id,
          displayName: s.name ?? s.email ?? s.user_id,
          roleLabel: s.role,
        });
      }
    }

    return [...byId.values()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
  }, [
    isOrgWide,
    orgUsersData,
    orgWideLearnersData,
    currentUserId,
  ]);

  const participantOptions = useMemo(() => {
    if (role === ROLES.COACH) return coachLearnerOptions;
    if (role === ROLES.STUDENT) return studentCoachOptions;
    if (isOrgWide) return orgWideOptions;
    return [];
  }, [
    role,
    coachLearnerOptions,
    studentCoachOptions,
    isOrgWide,
    orgWideOptions,
  ]);

  const participantsLoading = useMemo(() => {
    if (!open || !currentUserId) return false;
    if (isOrgWide) {
      return loadingOrgWide || loadingOrgWideLearners;
    }
    if (role === ROLES.COACH) {
      return (
        loadingUserOrgScope ||
        (membershipOrgIds.length > 0 && loadingCoachLearners)
      );
    }
    if (role === ROLES.STUDENT) {
      return (
        loadingUserOrgScope ||
        (membershipOrgIds.length > 0 && loadingCoaches)
      );
    }
    return false;
  }, [
    open,
    currentUserId,
    isOrgWide,
    loadingOrgWide,
    loadingOrgWideLearners,
    role,
    loadingUserOrgScope,
    membershipOrgIds.length,
    loadingCoachLearners,
    loadingCoaches,
  ]);

  const canSelectParticipant = useMemo(() => {
    if (isOrgWide) {
      if (role !== ROLES.ADMIN && loadingUserOrgScope) return true;
      return Boolean(orgWideOrgId);
    }
    if (role === ROLES.COACH || role === ROLES.STUDENT) {
      if (loadingUserOrgScope) return true;
      return membershipOrgIds.length > 0;
    }
    return false;
  }, [
    isOrgWide,
    orgWideOrgId,
    role,
    loadingUserOrgScope,
    membershipOrgIds.length,
  ]);

  const contextMessage = useMemo(() => {
    if (isOrgWide && !orgWideOrgId) {
      // Admins pick the organization themselves, and the scope query is still
      // in flight for everyone else until it resolves.
      if (role === ROLES.ADMIN || loadingUserOrgScope) return undefined;
      return "Your profile does not have an organization. You cannot start a chat until one is assigned.";
    }
    if (
      role === ROLES.COACH &&
      !loadingUserOrgScope &&
      membershipOrgIds.length === 0
    ) {
      return "You are not linked to any organization or class, so there are no learners to message.";
    }
    if (
      role === ROLES.STUDENT &&
      !loadingUserOrgScope &&
      membershipOrgIds.length === 0
    ) {
      return "You are not linked to any organization or class, so there are no coaches to message.";
    }
    return undefined;
  }, [
    isOrgWide,
    orgWideOrgId,
    role,
    loadingUserOrgScope,
    membershipOrgIds.length,
  ]);

  return {
    participantOptions,
    participantsLoading,
    canSelectParticipant,
    contextMessage,
  };
}
